/**
 * Utility Functions for Correspondences
 *
 * This module provides helper functions for correspondence-related operations,
 * including formatting, validation, and data transformations.
 */

import type {
  Correspondence,
  CorrespondenceType,
  CorrespondenceStatus
} from './types';
import { correspondenceKeys } from '@/lib/query-keys';

// ============================================================================
// Formatting Functions
// ============================================================================

/**
 * Format correspondence type for display
 */
export function formatCorrespondenceType(type: CorrespondenceType): string {
  const labels: Record<CorrespondenceType, string> = {
    notification: 'Notification',
    redemption: 'Redemption',
    'collection-request': 'Collection request',
    'follow-up': 'Follow-up',
    reply: 'Reply',
    acknowledgement: 'Acknowledgement',
    'ad-hoc': 'Email'
  };

  return labels[type] || type;
}

/**
 * Format correspondence status for display
 */
export function formatCorrespondenceStatus(
  status: CorrespondenceStatus
): string {
  const labels: Record<CorrespondenceStatus, string> = {
    draft: 'Draft',
    sending: 'Sending',
    sent: 'Sent',
    received: 'Received',
    quarantined: 'Send failed'
  };

  return labels[status] || status;
}

// ============================================================================
// Cache Management
// ============================================================================

/**
 * Generate cache key for correspondences query
 * @deprecated Use correspondenceKeys.forMatter() from @/lib/query-keys instead
 */
export function getCorrespondencesCacheKey(
  matterId: number
): readonly (string | number)[] {
  return correspondenceKeys.forMatter(matterId);
}

/**
 * Generate cache key for single correspondence query
 * @deprecated Use correspondenceKeys.item() from @/lib/query-keys instead
 */
export function getCorrespondenceCacheKey(
  matterId: number,
  correspondenceId: number
): readonly (string | number)[] {
  return correspondenceKeys.item(matterId, correspondenceId);
}

// ============================================================================
// Export Functions
// ============================================================================

/**
 * Export correspondences to CSV format
 */
export function exportCorrespondencesToCSV(
  correspondences: Correspondence[]
): string {
  const headers = [
    'Date',
    'Type',
    'Subject',
    'Institution',
    'Email To',
    'Status',
    'Updated Date'
  ];

  const rows = correspondences.map((corr) => [
    new Date(corr.createdAt).toLocaleDateString('en-AU'),
    formatCorrespondenceType(corr.type),
    corr.subject,
    corr.institution.name,
    corr.emailto || corr.institution.email || '',
    formatCorrespondenceStatus(corr.status),
    new Date(corr.updatedAt).toLocaleDateString('en-AU')
  ]);

  const csvContent = [
    headers.join(','),
    ...rows.map((row) => row.map((cell) => `"${cell}"`).join(','))
  ].join('\n');

  return csvContent;
}
